import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heart, ChevronLeft, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { GlassCard, PageLoading } from '../components/shared';
import { donationsApi } from '../services/api';
import RichTextRenderer from '../components/RichTextRenderer';
import DonationFormDialog from '../components/donations/DonationFormDialog';

const fmtAmount = (cents, currency = 'EUR') => {
  const value = (Number(cents || 0) / 100);
  try { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(value); }
  catch { return `${value.toFixed(2)} ${currency}`; }
};

const QuestDetail = () => {
  const { slug } = useParams();
  const [quest, setQuest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);

  useEffect(() => {
    setLoading(true);
    donationsApi.getQuestBySlug(slug)
      .then((r) => setQuest(r.data))
      .catch(() => setQuest(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <PageLoading />;
  if (!quest) {
    return (
      <div className="min-h-screen py-8 px-4 max-w-3xl mx-auto">
        <Link to="/dons" className="inline-flex items-center text-sm text-text-muted hover:text-primary mb-4">
          <ChevronLeft className="w-4 h-4 mr-1" />Retour aux dons
        </Link>
        <GlassCard className="text-center py-10">
          <p className="text-text-muted">Quête introuvable.</p>
        </GlassCard>
      </div>
    );
  }

  const goal = quest.goal_amount_cents || 0;
  const raised = quest.raised_amount_cents || 0;
  const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/dons" className="inline-flex items-center text-sm text-text-muted hover:text-primary mb-4" data-testid="quest-detail-back">
          <ChevronLeft className="w-4 h-4 mr-1" />Retour aux dons
        </Link>

        <GlassCard data-testid="quest-detail-card">
          <div className="flex items-start justify-between gap-2 mb-4 flex-wrap">
            <div>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-surface-elevated text-text-muted border border-border">
                {quest.category}
              </span>
              <h1 className="font-serif text-3xl text-text-primary mt-2">{quest.title_fr}</h1>
              {quest.title_ar && (
                <div className="font-arabic text-xl text-primary mt-1" dir="rtl">{quest.title_ar}</div>
              )}
            </div>
          </div>

          {goal > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between text-sm text-text-muted mb-1">
                <span>{fmtAmount(raised)} collectés</span>
                <span>Objectif : {fmtAmount(goal)}</span>
              </div>
              <div className="h-2 rounded bg-surface-elevated overflow-hidden">
                <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
              </div>
              <div className="text-xs text-text-muted mt-1 text-right">{pct}%</div>
            </div>
          )}

          {quest.description_html && (
            <RichTextRenderer
              value={quest.description_html}
              testid="quest-detail-description"
              className="mb-6"
            />
          )}

          <div className="mt-4 mb-6 flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs">
            <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <span className="text-amber-900 dark:text-amber-200/90">
              Les paiements en ligne seront bientôt disponibles. Vous pouvez déjà <strong>enregistrer un don</strong>.
            </span>
          </div>

          <Button
            onClick={() => setOpenForm(true)}
            className="bg-primary text-background w-full"
            data-testid="quest-detail-donate-btn"
          >
            <Heart className="w-4 h-4 mr-2" />Faire un don
          </Button>
        </GlassCard>
      </div>

      <DonationFormDialog
        open={openForm}
        quest={quest}
        onClose={() => setOpenForm(false)}
      />
    </div>
  );
};

export default QuestDetail;
